import { useRef } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { Sparkles, ContactShadows } from "@react-three/drei";
import { EffectComposer, Bloom } from "@react-three/postprocessing";
import * as THREE from "three";

const seg = (s, a, b) => THREE.MathUtils.smoothstep(s, a, b);

function Building({ progress }) {
    const smooth = useRef(0);
    const foundation = useRef(null);
    const columns = useRef([]);
    const slab = useRef(null);
    const wallsLower = useRef(null);
    const wallsUpper = useRef(null);
    const roof = useRef(null);
    const trimMesh = useRef(null);
    const glassMat = useRef(null);
    const glassMat2 = useRef(null);
    const roofMat = useRef(null);
    const trimMat = useRef(null);
    const waterMat = useRef(null);
    const interior1 = useRef(null);
    const interior2 = useRef(null);
    const rim = useRef(null);
    const trees = useRef([]);

    const white = useRef(
        new THREE.MeshStandardMaterial({
            color: "#e9e1d3",
            roughness: 0.45,
            metalness: 0.03,
        }),
    );
    const concrete = useRef(
        new THREE.MeshStandardMaterial({ color: "#cfccc4", roughness: 0.65 }),
    );
    const teak = useRef(
        new THREE.MeshStandardMaterial({ color: "#6f4a2e", roughness: 0.55 }),
    );

    useFrame((state, delta) => {
        smooth.current = THREE.MathUtils.damp(
            smooth.current,
            progress.get(),
            4.5,
            delta,
        );
        const s = smooth.current;

        if (foundation.current) {
            const v = seg(s, 0, 0.12);
            foundation.current.visible = v > 0.005;
            foundation.current.scale.y = Math.max(0.001, v);
        }

        columns.current.forEach((c, i) => {
            if (c) {
                const v = seg(s, 0.12 + i * 0.02, 0.3);
                c.visible = v > 0.005;
                c.scale.y = Math.max(0.001, v);
            }
        });

        if (slab.current) {
            const v = seg(s, 0.28, 0.38);
            slab.current.visible = v > 0.005;
            slab.current.scale.set(Math.max(0.001, v), 1, Math.max(0.001, v));
        }
        if (wallsLower.current) {
            const v = seg(s, 0.36, 0.5);
            wallsLower.current.visible = v > 0.005;
            wallsLower.current.scale.y = Math.max(0.001, v);
        }
        if (wallsUpper.current) {
            const v = seg(s, 0.48, 0.6);
            wallsUpper.current.visible = v > 0.005;
            wallsUpper.current.scale.y = Math.max(0.001, v);
        }

        if (glassMat.current) glassMat.current.opacity = seg(s, 0.58, 0.7) * 0.55;
        if (glassMat2.current) glassMat2.current.opacity = seg(s, 0.62, 0.72) * 0.5;

        if (roof.current) {
            const r = seg(s, 0.68, 0.78);
            roof.current.visible = r > 0.01;
            roof.current.position.y = 5.2 + (1 - r) * 4.5;
        }
        if (roofMat.current) roofMat.current.opacity = seg(s, 0.68, 0.78);
        if (trimMesh.current)
            trimMesh.current.visible = seg(s, 0.68, 0.8) > 0.01;
        if (trimMat.current) {
            trimMat.current.emissiveIntensity = seg(s, 0.76, 0.84) * 1.6;
            trimMat.current.opacity = seg(s, 0.68, 0.8) * 0.95;
        }

        if (interior1.current)
            interior1.current.intensity = seg(s, 0.78, 0.88) * 28;
        if (interior2.current)
            interior2.current.intensity = seg(s, 0.8, 0.9) * 22;
        if (rim.current) rim.current.intensity = seg(s, 0.9, 1) * 1.6;

        if (waterMat.current) waterMat.current.opacity = seg(s, 0.86, 0.94) * 0.92;

        trees.current.forEach((t, i) => {
            if (t) {
                const v = seg(s, 0.86 + i * 0.03, 0.97);
                t.scale.setScalar(Math.max(0.001, v));
            }
        });

        const angle = 0.9 + s * 2.4;
        const aspect = state.size.width / state.size.height;
        const rf = THREE.MathUtils.clamp(1.35 / aspect, 1, 2.6);
        const radius = (14.5 - s * 2.0) * rf;
        state.camera.position.x = Math.sin(angle) * radius;
        state.camera.position.z = Math.cos(angle) * radius;
        state.camera.position.y = 3.2 + s * 2.0;
        state.camera.lookAt(0, 1.2 + s * 1.4, 0);
    });

    return (
        <group scale={1.05}>
            {/* foundation */}
            <group ref={foundation} position={[0, 0, 0]}>
                <mesh position={[0, 0.25, 0]} material={concrete.current} castShadow receiveShadow>
                    <boxGeometry args={[7.6, 0.5, 5.6]} />
                </mesh>
            </group>

            {/* columns */}
            {[[-2.4, -1.8], [2.4, -1.8], [-2.4, 1.8], [2.4, 1.8]].map(
                ([x, z], i) => (
                    <group
                        key={`${x}${z}`}
                        position={[x, 0.5, z]}
                        ref={(el) => (columns.current[i] = el)}
                    >
                        <mesh position={[0, 1.15, 0]} material={teak.current} castShadow>
                            <cylinderGeometry args={[0.13, 0.13, 2.3, 16]} />
                        </mesh>
                    </group>
                ),
            )}

            {/* first floor slab */}
            <mesh
                ref={slab}
                position={[0, 2.9, 0]}
                material={concrete.current}
                castShadow
            >
                <boxGeometry args={[6.7, 0.18, 4.7]} />
            </mesh>

            {/* lower walls */}
            <group ref={wallsLower} position={[0, 0.5, 0]}>
                <mesh position={[0, 1.15, 0]} material={white.current} castShadow>
                    <boxGeometry args={[5.7, 2.3, 3.9]} />
                </mesh>
            </group>

            {/* lower glass */}
            <mesh position={[0, 1.65, 1.99]}>
                <boxGeometry args={[3.6, 1.9, 0.06]} />
                <meshPhysicalMaterial
                    ref={glassMat}
                    color="#b9d8e4"
                    roughness={0.05}
                    metalness={0.1}
                    transparent
                    opacity={0}
                />
            </mesh>

            {/* upper volume */}
            <group ref={wallsUpper} position={[0.75, 2.99, 0]}>
                <mesh position={[0, 1.05, 0]} material={white.current} castShadow>
                    <boxGeometry args={[4.9, 2.1, 3.5]} />
                </mesh>
            </group>

            {/* upper glass */}
            <mesh position={[0.75, 4.0, 1.79]}>
                <boxGeometry args={[3.9, 1.6, 0.06]} />
                <meshPhysicalMaterial
                    ref={glassMat2}
                    color="#b9d8e4"
                    roughness={0.05}
                    metalness={0.1}
                    transparent
                    opacity={0}
                />
            </mesh>

            {/* roof */}
            <mesh ref={roof} position={[0.75, 9.7, 0]} castShadow>
                <boxGeometry args={[5.7, 0.16, 4.1]} />
                <meshStandardMaterial
                    ref={roofMat}
                    color="#6b4d33"
                    roughness={0.5}
                    transparent
                    opacity={0}
                />
            </mesh>
            {/* gold trim */}
            <mesh ref={trimMesh} position={[0.75, 5.06, 0]}>
                <boxGeometry args={[5.1, 0.05, 3.7]} />
                <meshStandardMaterial
                    ref={trimMat}
                    color="#C8A96A"
                    emissive="#C8A96A"
                    emissiveIntensity={0}
                    transparent
                    opacity={0.95}
                />
            </mesh>

            {/* reflecting pool */}
            <mesh position={[3.1, 0.56, 2.1]}>
                <boxGeometry args={[2.6, 0.12, 1.7]} />
                <meshStandardMaterial
                    ref={waterMat}
                    color="#0d2836"
                    roughness={0.04}
                    metalness={0.9}
                    transparent
                    opacity={0}
                />
            </mesh>

            {/* landscape */}
            {[[-3.1, -2.3, 1.4], [3.4, -1.7, 1.1], [-2.9, 2.2, 0.9]].map(
                ([x, z, h], i) => (
                    <group
                        key={i}
                        position={[x, 0.5, z]}
                        ref={(el) => (trees.current[i] = el)}
                        scale={0.001}
                    >
                        <mesh position={[0, h * 0.35, 0]}>
                            <cylinderGeometry args={[0.06, 0.08, h * 0.7, 8]} />
                            <meshStandardMaterial color="#4a3b2c" roughness={0.9} />
                        </mesh>
                        <mesh position={[0, h * 0.95, 0]} castShadow>
                            <coneGeometry args={[h * 0.42, h * 1.1, 8]} />
                            <meshStandardMaterial color="#2c4a28" roughness={0.8} />
                        </mesh>
                    </group>
                ),
            )}

            {/* interior warm lights */}
            <pointLight
                ref={interior1}
                position={[0.75, 4.1, 0]}
                color="#ffc98a"
                intensity={0}
                distance={8}
                decay={2}
            />
            <pointLight
                ref={interior2}
                position={[0, 1.7, 0.8]}
                color="#ffc98a"
                intensity={0}
                distance={7}
                decay={2}
            />
            <directionalLight
                ref={rim}
                position={[-6, 3, -6]}
                color="#C8A96A"
                intensity={0}
            />
            {/* warm ground glow so early stages never look empty */}
            <pointLight
                position={[4, 1.2, 4]}
                color="#C8A96A"
                intensity={10}
                distance={16}
                decay={2}
            />

            {/* ground */}
            <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.01, 0]} receiveShadow>
                <circleGeometry args={[13, 64]} />
                <meshStandardMaterial color="#0d0d0d" roughness={0.4} metalness={0.55} />
            </mesh>
            <gridHelper args={[26, 52, "#333333", "#1a1a1a"]} position={[0, 0, 0]} />
            <ContactShadows
                position={[0, 0.01, 0]}
                opacity={0.55}
                scale={20}
                blur={2.4}
                far={8}
            />
        </group>
    );
}

export default function ConstructionScene({ progress }) {
    return (
        <Canvas
            shadows
            dpr={[1, 1.5]}
            camera={{ position: [9, 3.2, 9], fov: 36 }}
            gl={{ antialias: true, alpha: true }}
            aria-label="Scroll-driven 3D visualization of a villa being constructed"
        >
            <fog attach="fog" args={["#0B0B0B", 14, 40]} />
            <ambientLight intensity={0.55} />
            <directionalLight
                position={[7, 10, 5]}
                intensity={2}
                castShadow
                shadow-mapSize={[1024, 1024]}
                shadow-bias={-0.0004}
            />
            <Building progress={progress} />
            <Sparkles
                count={40}
                scale={[16, 9, 16]}
                position={[0, 4, 0]}
                size={1.6}
                speed={0.12}
                opacity={0.4}
                color="#C8A96A"
            />
            <EffectComposer>
                <Bloom
                    intensity={0.4}
                    luminanceThreshold={0.75}
                    luminanceSmoothing={0.25}
                    mipmapBlur
                />
            </EffectComposer>
        </Canvas>
    );
}
